import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FiArrowUpRight, FiLock } from "react-icons/fi";
import { projects, projectFilters } from "../data/projects";
import Reveal from "./ui/Reveal";
import SectionHeading from "./ui/SectionHeading";

const ProjectCard = ({ project, index }) => {
  const isLive = Boolean(project.link);
  const Wrapper = isLive ? "a" : "div";

  const wrapperProps = isLive
    ? {
        href: project.link,
        target: "_blank",
        rel: "noopener noreferrer",
      }
    : {};

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.5, delay: Math.min(index, 6) * 0.04 }}
      className={`group relative ${
        project.featured ? "md:col-span-3" : "md:col-span-2"
      }`}
    >
      <Wrapper
        {...wrapperProps}
        className="flex h-full flex-col overflow-hidden rounded-3xl border border-ink/[0.08] bg-white transition duration-500 hover:-translate-y-2 hover:border-moss/25 hover:shadow-lift"
      >
        <div className="relative overflow-hidden bg-bone">
          <img
            src={project.image}
            alt={`${project.title} preview`}
            loading="lazy"
            decoding="async"
            width={1200}
            height={840}
            className={`w-full object-cover object-top transition-transform duration-[900ms] ease-out group-hover:scale-[1.06] ${
              project.featured ? "h-64 sm:h-80" : "h-56"
            }`}
          />

          <div className="absolute inset-0 bg-gradient-to-t from-ink/45 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

          <span className="absolute left-4 top-4 rounded-full bg-ink/80 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-mint">
            {project.year}
          </span>

          <span
            className={`absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full transition duration-500 ${
              isLive
                ? "bg-mint text-ink opacity-0 group-hover:opacity-100"
                : "bg-ink/70 text-cream/70"
            }`}
            aria-hidden="true"
          >
            {isLive ? <FiArrowUpRight /> : <FiLock className="text-sm" />}
          </span>
        </div>

        <div className="flex flex-1 flex-col p-7">
          <p className="eyebrow text-moss/70">{project.category}</p>

          <h3 className="mt-3 font-display text-2xl leading-tight text-ink transition-colors duration-300 group-hover:text-moss sm:text-[1.7rem]">
            {project.title}
          </h3>

          <p className="mt-3 flex-1 text-[14.5px] leading-7 text-stone">
            {project.description}
          </p>

          <div className="mt-6 flex flex-wrap gap-2">
            {project.tech.map((item) => (
              <span
                key={item}
                className="rounded-full border border-ink/[0.08] bg-cream px-3 py-1.5 text-[11px] font-medium text-pine"
              >
                {item}
              </span>
            ))}
          </div>

          <div className="mt-6 flex items-center gap-2 border-t border-ink/[0.07] pt-5 text-[13px] font-semibold text-ink/70">
            {isLive ? (
              <>
                Visit live site
                <FiArrowUpRight className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </>
            ) : (
              <>
                <FiLock className="text-xs" />
                Private project
              </>
            )}
          </div>
        </div>
      </Wrapper>
    </motion.article>
  );
};

const Projects = () => {
  const [filter, setFilter] = useState("All");

  const visible = useMemo(
    () =>
      filter === "All"
        ? projects
        : projects.filter((project) => project.tags.includes(filter)),
    [filter]
  );

  return (
    <section id="projects" className="section bg-bone">
      <div className="pointer-events-none absolute -right-40 top-40 h-[460px] w-[460px] rounded-full glow-frost" />

      <div className="shell relative">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading
            index="02"
            eyebrow="Selected work"
            title="Things I’ve designed,"
            accent="built and shipped."
            tone="light"
          />

          <Reveal delay={0.1} className="shrink-0">
            <div className="flex flex-wrap gap-2">
              {projectFilters.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setFilter(item)}
                  className={`rounded-full border px-5 py-2.5 text-[13px] font-medium transition duration-300 ${
                    filter === item
                      ? "border-pine bg-pine text-cream"
                      : "border-ink/10 bg-white/70 text-stone hover:border-moss/30 hover:text-ink"
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-6">
          <AnimatePresence mode="popLayout">
            {visible.map((project, index) => (
              <ProjectCard
                key={project.title}
                project={project}
                index={index}
              />
            ))}
          </AnimatePresence>
        </div>

        <Reveal delay={0.1}>
          <p className="mt-12 text-center text-sm text-stone">
            Some client and internship work stays private —{" "}
            <a
              href="#contact"
              className="link-underline font-semibold text-pine"
            >
              ask me about it
            </a>{" "}
            and I’ll walk you through it.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default Projects;
